import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useTheme } from '@/contexts/ThemeContext';
import { HardHat, LayoutGrid, FileUp, BarChart3, ShieldCheck, Hammer, CableCar, Cog, Box, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import SWMDataImport from '@/components/structural/SWMDataImport';
import SWMValidation from '@/components/structural/SWMValidation';
import SWMSummaryDashboard from '@/components/structural/SWMSummaryDashboard';
import SWMWorkfrontStatus from '@/components/structural/SWMWorkfrontStatus';
import SWMFabProgress from '@/components/structural/SWMFabProgress';
import SWMErectionProgress from '@/components/structural/SWMErectionProgress';
import SWMAllocationEngine from '@/components/structural/SWMAllocationEngine';
import SWMAnalytics from '@/components/structural/SWMAnalytics';
import WorkfrontModelTab from '@/components/workfront/WorkfrontModelTab';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import ModuleHelp from '@/components/common/ModuleHelp';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';

import { useProject } from '@/contexts/ProjectContext';

const TABS = [
  { id: 'summary', label: 'Summary', icon: BarChart3 },
  { id: 'insights', label: 'AI Insights', icon: Sparkles },
  { id: 'workfront', label: 'Workfront Status', icon: LayoutGrid },
  { id: 'fabrication', label: 'Fabrication', icon: Hammer },
  { id: 'erection', label: 'Erection', icon: CableCar },
  { id: '3dmodel', label: '3D Model', icon: Box },
  { id: 'allocation', label: 'Allocation Engine', icon: Cog },
  { id: 'import', label: 'Data & Registers', icon: FileUp },
  { id: 'validation', label: 'Validation', icon: ShieldCheck },
  { id: 'analytics', label: 'KPIs & Analytics', icon: BarChart3 },
];

export default function StructuralWorkfront() {
  const { isDark } = useTheme();
  const { projectId: PROJECT_ID, selectedProject } = useProject();
  const [activeTab, setActiveTab] = useState('summary');
  const [searchParams] = useSearchParams();
  const [globalFilters, setGlobalFilters] = useState({ area: '', priority: '', workfrontStatus: '' });
  const [filterOptions, setFilterOptions] = useState({ areas: [], structureTypes: [] });

  // Reset state and re-fetch when project changes
  useEffect(() => {
    if (!PROJECT_ID) return;
    setActiveTab('summary');
    // Pre-apply filters from URL search params (cross-module navigation)
    const urlArea = searchParams.get('area') || '';
    const urlPriority = searchParams.get('priority') || '';
    const urlStatus = searchParams.get('workfrontStatus') || '';
    setGlobalFilters({ area: urlArea, priority: urlPriority, workfrontStatus: urlStatus });
    setFilterOptions({ areas: [], structureTypes: [] });
    (async () => {
      try {
        const res = await api.get(`/v1/projects/${PROJECT_ID}/swm/filter-options`);
        setFilterOptions(res.data || {});
      } catch { /* ignore */ }
    })();
  }, [PROJECT_ID]);

  return (
    <DashboardLayout>
      <div className="space-y-3 p-4 md:p-6" data-testid="structural-workfront-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: '#F5920620', border: '1px solid #F5920640' }}>
              <HardHat className="w-5 h-5" style={{ color: '#F59206' }} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Structural Workfront Management</h1>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                Material ordering, allocation, fabrication & erection tracking for structural steel
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ModuleHelp moduleKey="swm" />
            <span className="text-xs px-3 py-1.5 rounded-lg" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}
              data-testid="swm-project-label">Project: {selectedProject?.name || PROJECT_ID}</span>
          </div>
        </div>

        {/* AI Analyst Panel */}
        {PROJECT_ID && (
          <AIAnalystPanel projectId={PROJECT_ID} moduleKey="STRUCTURAL_WORKFRONT" defaultOpen={false} compact={true} />
        )}

        {/* Global Filters */}
        <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }} data-testid="swm-global-filters">
          <select value={globalFilters.area} onChange={e => setGlobalFilters(f => ({ ...f, area: e.target.value }))} data-testid="swm-filter-area"
            className="px-2 py-1 text-[10px] rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Areas</option>
            {(filterOptions.areas || []).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={globalFilters.priority} onChange={e => setGlobalFilters(f => ({ ...f, priority: e.target.value }))} data-testid="swm-filter-priority"
            className="px-2 py-1 text-[10px] rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Priorities</option>
            {(filterOptions.priorities || [1, 2, 3, 4, 5]).map(p => <option key={p} value={p}>P{p}</option>)}
          </select>
          <select value={globalFilters.workfrontStatus} onChange={e => setGlobalFilters(f => ({ ...f, workfrontStatus: e.target.value }))} data-testid="swm-filter-status"
            className="px-2 py-1 text-[10px] rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Statuses</option>
            {(filterOptions.statuses || ['UNALLOCATED', 'PARTIAL_SOFT', 'READY_SOFT', 'PO_READY', 'PARTIAL_HARD', 'FULLY_ALLOCATED']).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                data-testid={`swm-tab-${tab.id}`}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap"
                style={{
                  background: isActive ? 'var(--card-bg)' : 'transparent',
                  color: isActive ? '#F59206' : 'var(--text-muted)',
                  borderBottom: isActive ? '2px solid #F59206' : '2px solid transparent',
                }}>
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content — each tab wrapped in its own ErrorBoundary so a
            render-time crash in one panel never blanks the whole page (it
            was surfacing as an opaque `Script error.` overlay before). */}
        <div data-testid="swm-tab-content">
          {activeTab === 'summary'     && <ErrorBoundary name="swm-summary"    title="Summary panel hit an error"><SWMSummaryDashboard projectId={PROJECT_ID} filters={globalFilters} /></ErrorBoundary>}
          {activeTab === 'insights'    && <ErrorBoundary name="swm-insights"   title="AI Insights panel hit an error"><ModuleInsightsDashboard projectId={PROJECT_ID} moduleKey="swm" color="#F59206" deepLinkBase="/structural-workfront" /></ErrorBoundary>}
          {activeTab === 'workfront'   && <ErrorBoundary name="swm-workfront"  title="Workfront Status panel hit an error"><SWMWorkfrontStatus projectId={PROJECT_ID} filters={globalFilters} /></ErrorBoundary>}
          {activeTab === 'fabrication' && <ErrorBoundary name="swm-fab"        title="Fabrication panel hit an error"><SWMFabProgress projectId={PROJECT_ID} filters={globalFilters} /></ErrorBoundary>}
          {activeTab === 'erection'    && <ErrorBoundary name="swm-erection"   title="Erection panel hit an error"><SWMErectionProgress projectId={PROJECT_ID} filters={globalFilters} /></ErrorBoundary>}
          {activeTab === '3dmodel'     && <ErrorBoundary name="swm-3d"         title="3D Model panel hit an error"><WorkfrontModelTab projectId={PROJECT_ID} moduleKey="structural" /></ErrorBoundary>}
          {activeTab === 'allocation'  && <ErrorBoundary name="swm-allocation" title="Allocation Engine panel hit an error"><SWMAllocationEngine projectId={PROJECT_ID} /></ErrorBoundary>}
          {activeTab === 'import'      && <ErrorBoundary name="swm-import"     title="Import panel hit an error"><SWMDataImport projectId={PROJECT_ID} /></ErrorBoundary>}
          {activeTab === 'validation'  && <ErrorBoundary name="swm-validation" title="Validation panel hit an error"><SWMValidation projectId={PROJECT_ID} /></ErrorBoundary>}
          {activeTab === 'analytics'   && <ErrorBoundary name="swm-analytics"  title="Analytics panel hit an error"><SWMAnalytics projectId={PROJECT_ID} /></ErrorBoundary>}
        </div>
      </div>
    </DashboardLayout>
  );
}
